import { BLOCK_APPOINTMENT_TYPE_CODE, ProviderRole } from '@bariendo/lib/constants';
import { getReferenceString, resolveId } from '@medplum/core';
import { Practitioner, PractitionerRole, Reference, Slot } from '@medplum/fhirtypes';
import { BlockSlotEvent } from '../types';
import { AppointmentsFilters } from '../hooks/useAppointments';

/**
 * PractitionerRole refs to load appointment-availability schedules (same shape as loadAvailabilityResources).
 */
export function practitionerRoleRefsForCalendarFetch(
  practitionerRoles: PractitionerRole[],
  filters: AppointmentsFilters
): Reference<PractitionerRole>[] {
  if (filters.practitioners.length > 0) {
    const want = new Set(filters.practitioners.map((p) => getReferenceString(p)));
    return practitionerRoles
      .filter((r) => r.practitioner && want.has(getReferenceString(r.practitioner)))
      .map((r) => ({ reference: getReferenceString(r) }) as Reference<PractitionerRole>);
  }
  if (filters.roles.length > 0) {
    const roleCodes = new Set(filters.roles);
    return practitionerRoles
      .filter((r) => {
        const codes = r.code?.flatMap((c) => c.coding?.map((co) => co.code) ?? []) ?? [];
        return codes.some((c) => c && roleCodes.has(c as ProviderRole));
      })
      .map((r) => ({ reference: getReferenceString(r) }) as Reference<PractitionerRole>);
  }
  return practitionerRoles
    .map((r) => getReferenceString(r))
    .filter(Boolean)
    .map((ref) => ({ reference: ref }) as Reference<PractitionerRole>);
}

export function slotOverlapsServiceTypeFilter(slot: Slot, selectedServiceTypes: string[]): boolean {
  if (selectedServiceTypes.length === 0) return true;
  const codes = slot.serviceType?.flatMap((st) => st.coding?.map((c) => c.code).filter(Boolean) as string[]) ?? [];
  if (codes.length === 0) return true;
  // Marker-only blocks (`calendar-block`) block the whole calendar — keep visible even when the
  // sidebar filter lists only clinical visit types (those never include `calendar-block`).
  if (codes.length === 1 && codes[0] === BLOCK_APPOINTMENT_TYPE_CODE) return true;
  return codes.some((c) => selectedServiceTypes.includes(c));
}

export function slotToBlockCalendarEvent(
  slot: Slot,
  practitionerRef: Reference<Practitioner>,
  practitioners: Practitioner[]
): BlockSlotEvent {
  const refStr = getReferenceString(practitionerRef) ?? '';
  const resolved = practitioners.find((p) => getReferenceString(p) === refStr || p.id === resolveId(practitionerRef));
  const providers = resolved ? [resolved] : practitionerRef.reference ? [practitionerRef] : [];

  return {
    title: 'Block',
    start: new Date(slot.start as string),
    end: new Date(slot.end as string),
    slotResource: slot,
    providers,
    patients: [],
    resource: refStr,
  } as BlockSlotEvent;
}
